// turnkey-signer-frame.ts - a HIDDEN, offscreen iframe of the Bounded signer page
// for UI-less signer exchanges: sealing a login code (bounded:turnkey:encryptOtp),
// establishing the 24h signing session right after login
// (bounded:turnkey:establishSigningSession), and destroying it on logout
// (bounded:turnkey:clearSigningSession).
//
// One handle = one live frame. The frame is kept across requests so multi-step
// exchanges (seal a code, then establish the session with the keypair the SEAL
// left in that exact page instance) talk to the same page. Origin + source +
// requestId are all pinned; dispose() tears everything down.
import { getConfig } from '@bounded-sh/core';
function hasDOM() {
    return typeof window !== 'undefined' && typeof document !== 'undefined';
}
async function signerPage() {
    const cfg = await getConfig();
    const base = (cfg.humanAuthApiUrl || 'https://auth.bounded.sh').replace(/\/$/, '');
    return { url: `${base}/wallet/turnkey/signer`, origin: new URL(base).origin };
}
/** Mount the hidden signer frame. Callers MUST dispose() when done. */
export async function openHiddenSignerFrame() {
    if (!hasDOM())
        throw new Error('The Bounded signer frame is only available in the browser.');
    const { url, origin } = await signerPage();
    const frame = document.createElement('iframe');
    frame.setAttribute('aria-hidden', 'true');
    frame.style.cssText =
        'position:fixed;width:1px;height:1px;left:-9999px;top:-9999px;border:0;opacity:0;pointer-events:none';
    let disposed = false;
    let readyResolve = null;
    const ready = new Promise((resolve) => { readyResolve = resolve; });
    const pending = new Map();
    const onMsg = (event) => {
        if (event.origin !== origin || event.source !== frame.contentWindow)
            return;
        const d = event.data;
        if (!d || typeof d !== 'object')
            return;
        if (d.type === 'bounded:turnkey:ready') {
            readyResolve === null || readyResolve === void 0 ? void 0 : readyResolve();
            return;
        }
        if (d.type === 'bounded:turnkey:result' && typeof d.requestId === 'string') {
            const settle = pending.get(d.requestId);
            if (settle) {
                pending.delete(d.requestId);
                settle(d);
            }
        }
    };
    window.addEventListener('message', onMsg);
    frame.src = url;
    document.body.appendChild(frame);
    const dispose = () => {
        if (disposed)
            return;
        disposed = true;
        window.removeEventListener('message', onMsg);
        pending.clear();
        try {
            frame.remove();
        }
        catch ( /* already gone */_a) { /* already gone */ }
    };
    return {
        dispose,
        request(message, timeoutMs) {
            const requestId = String(message.requestId || '');
            if (!requestId)
                return Promise.reject(new Error('signer frame request needs a requestId'));
            if (disposed)
                return Promise.reject(new Error('signer frame disposed'));
            return new Promise((resolve, reject) => {
                let timer;
                pending.set(requestId, (d) => {
                    if (timer)
                        clearTimeout(timer);
                    resolve(d);
                });
                timer = setTimeout(() => {
                    pending.delete(requestId);
                    reject(new Error('The Bounded signer did not respond in time.'));
                }, timeoutMs);
                ready.then(() => {
                    var _a;
                    if (disposed || !pending.has(requestId))
                        return;
                    try {
                        (_a = frame.contentWindow) === null || _a === void 0 ? void 0 : _a.postMessage(message, origin);
                    }
                    catch ( /* frame gone; timeout rejects */_b) { /* frame gone; timeout rejects */ }
                });
            });
        },
    };
}
